import { InkToolbar, InkSurface } from './DocumentInk.js';
import { INK_COLORS, InkUndoHistory, type InkTool } from '../lib/documentInk.js';
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Copy, FileText, Minus, Plus, RefreshCw, Signature, Type, X } from 'lucide-react';
import { LIMITS, anchorSigner, isSenderBox, isSenderSignature, type SignatureAnchor } from '@worki/shared';
import { authedFetch } from '../lib/api.js';
import {
  addBox,
  anchorRole,
  boxView,
  dragAnchor,
  senderSignatureOf,
  signAsSender,
  typedFontSize,
  withRole,
  type AnchorRole,
  type BoxFrame,
} from '../lib/anchors.js';
import { BOX_OUTLINE, INPUT_OUTLINE, boxOutline, pageTap } from '../lib/boxOutline.js';
import { copyForNextSigner } from '../lib/nextSigner.js';
import { standardFontDataUrl } from '../lib/pdfFonts.js';
import { cn } from '../lib/cn.js';
import { MAX_ZOOM, MIN_ZOOM, usePinchZoom } from '../lib/usePinchZoom.js';
import { AnchoredMenu } from './AnchoredMenu.js';
import { PlaceRoleOptions, SenderSignatureDialog } from './PlaceDialogs.js';

/**
 * SIG-003 — the document, with the places the signature goes marked on it.
 *
 * One component for both sides of the link. The business opens it to say where
 * the signature belongs; the customer opens the same view, read-only, and sees
 * the boxes before drawing anything. That is deliberate — a signer who cannot
 * see where their name will land is signing a blank.
 *
 * Placing is a click on the page, not a drag: a drag on a phone is a scroll,
 * and the screen this is most used on is a phone. A tap puts a box for the
 * recipient's signature where the finger went, with its bar under it, and the
 * bar's signature and text buttons change what goes there: the recipient's
 * signature or words, or the sender's own (1.1, a third time). It is resized
 * with buttons, which a finger can hit and a drag handle three pixels wide
 * cannot.
 *
 * A box that is already down can be dragged, and only once it has been tapped
 * — asked for by the person who uses this screen, who was placing a box a few
 * millimetres off the printed line and having to delete it and try again. The
 * selection is what turns the gesture on, so a finger landing on an unselected
 * box still scrolls the document as every other part of it does; only the one
 * box being worked on holds the gesture (`touch-action: none`), and it holds it
 * for as long as it stays selected.
 *
 * The pages are painted by pdf.js — see PdfPreview for why the browser's own
 * viewer is not used — and the boxes are ordinary absolutely-positioned DOM on
 * top, so a placement is a percentage of the page rather than a pixel on this
 * particular screen.
 *
 * 1.18 — two fingers make the pages bigger, to put a box exactly where it goes.
 * The box the pages scroll in gives one finger to the scroll and two to the
 * pinch (`touch-action: pan-x pan-y`, so the screen itself does not zoom). The
 * pages grow as a live scale while the fingers are down and are painted again at
 * the new size when they lift, with the place that was under the fingers put
 * back under them. A box is a fraction of its page, so it grows with it; and a
 * drag that was under way is let go of, because the second finger is the pinch's.
 */

/** A new box, as a fraction of the page: about a signature line's worth. */
const DEFAULT_WIDTH = 0.3;
const DEFAULT_HEIGHT = 0.09;
const MIN_WIDTH = 0.08;
const MAX_WIDTH = 0.9;
const STEP = 1.25;

/** How far a finger travels before it is a drag and not a tap, in pixels. */
const DRAG_SLOP = 4;

/**
 * The colour typing is drawn in on the page: the stamp's own, rgb(0.05, 0.05,
 * 0.2). Fixed rather than the theme's, because the page under it is a white
 * sheet of paper in dark mode too, and the theme's text there is near white.
 */
const INK = '#0d0d33';

/* One canvas for every measurement, made the first time a line is measured. */
let measuring: CanvasRenderingContext2D | null | undefined;

/**
 * 1.1 — a line's width at a font size of one pixel, in the font the page is set
 * in, for `typedFontSize` to fit the line into its box.
 *
 * Measured at a hundred pixels and scaled down, because a canvas rounds a
 * one-pixel font to nothing. A browser with no canvas gets an estimate a little
 * wider than Heebo's letters, which errs towards a line set too small rather
 * than one cut off.
 */
function widthAtOnePixel(text: string): number {
  if (!text) return 0;
  if (measuring === undefined) measuring = window.document.createElement('canvas').getContext('2d');
  if (!measuring) return text.length * 0.6;
  measuring.font = `100px ${getComputedStyle(window.document.body).fontFamily}`;
  return measuring.measureText(text).width / 100;
}

interface RenderedPage {
  number: number;
  canvas: HTMLCanvasElement;
  /** In CSS pixels, at a zoom of 1: the width that fits the box. */
  width: number;
  height: number;
}

/**
 * The document's bytes, from wherever it is.
 *
 * A file that has not been uploaded yet is passed as the file itself. The
 * obvious alternative — a `blob:` address minted for it — reads like a URL but
 * is fetched like one too, and `connect-src 'self'` refuses that: the pages
 * came up empty in production while working in development, which serves no
 * policy at all. Bytes already in this browser's hands need no address.
 */
async function bytesOf(src: string | Blob): Promise<ArrayBuffer> {
  if (typeof src !== 'string') return src.arrayBuffer();
  const response = await authedFetch(src);
  if (!response.ok) throw new Error(String(response.status));
  return response.arrayBuffer();
}

/**
 * 1.18 — the most pixels a canvas is painted with along its longer side. A
 * phone's browser drops a canvas larger than 4096 × 4096 without a word, and the
 * page comes up blank.
 */
export const MAX_CANVAS_SIDE = 4096;
/** And all the canvases together: three of the largest, about 200 MB of pixels. */
export const MAX_CANVAS_PIXELS = 3 * MAX_CANVAS_SIDE * MAX_CANVAS_SIDE;

/**
 * 1.18 — how many pixels to paint for each one a page takes up at a zoom of 1.
 *
 * The zoom times the screen's own density, so a page pinched bigger is sharper
 * and not only larger. Held to what a phone will hold — one canvas at 4096 on its
 * long side, all of them inside a budget — because a long document at 4× would
 * otherwise be gigabytes, and a browser that runs out takes the tab, and the
 * boxes placed so far, with it. A long document is blurrier when zoomed rather
 * than lost. The caps only bite once zoomed in: at 1× the pages are painted at
 * the screen's density, as they always were, however long the document is.
 */
export function canvasDensity(
  pages: readonly { width: number; height: number }[],
  zoom: number,
  density: number,
): number {
  const longest = Math.max(1, ...pages.map((page) => Math.max(page.width, page.height)));
  const area = Math.max(1, pages.reduce((sum, page) => sum + page.width * page.height, 0));
  const room = Math.min(MAX_CANVAS_SIDE / longest, Math.sqrt(MAX_CANVAS_PIXELS / area));
  return Math.max(Math.min(zoom * density, room), density);
}

/**
 * 1.18 — what a second finger does to a drag: it was never one. The box goes back
 * to where the first finger found it. `null` says there is nothing to put back,
 * because the box never left.
 */
export function undoDrag(
  anchors: SignatureAnchor[],
  drag: { index: number; origin: SignatureAnchor; moved: boolean } | null,
): SignatureAnchor[] | null {
  if (!drag?.moved) return null;
  return dragAnchor(anchors, drag.index, drag.origin, 0, 0);
}

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A place on a page, as a fraction of it across and down: the same place at any zoom. */
interface Place {
  page: number;
  u: number;
  v: number;
}

/**
 * 1.18 — the place on the pages that is under a point on the screen, and so what
 * to keep under the fingers when the pages are laid out again. The page the point
 * is on; between two pages or past either end, the nearest, and then the
 * fraction runs a little past the page's own edge, which is as good to come back to.
 */
export function placeUnder(pages: readonly { number: number; rect: Rect }[], x: number, y: number): Place | null {
  let nearest: { number: number; rect: Rect; away: number } | null = null;
  for (const { number, rect } of pages) {
    const away = Math.max(rect.top - y, y - (rect.top + rect.height), 0);
    if (!nearest || away < nearest.away) nearest = { number, rect, away };
  }
  if (!nearest) return null;
  return { page: nearest.number, u: (x - nearest.rect.left) / nearest.rect.width, v: (y - nearest.rect.top) / nearest.rect.height };
}

/** Where a place is on the screen, on its page as it is now. */
export function pointOn(place: Place, rect: Rect): { x: number; y: number } {
  return { x: rect.left + place.u * rect.width, y: rect.top + place.v * rect.height };
}

/** Where each page is on the screen, by its number. */
function pageRects(box: HTMLElement) {
  return Array.from(box.querySelectorAll<HTMLElement>('[data-page]'), (element) => ({
    number: Number(element.dataset.page),
    rect: element.getBoundingClientRect(),
  }));
}

/**
 * Where the selected box's controls go on its page: under the box, starting at
 * its left edge.
 *
 * 1.1, follow-up — and moved left as far as it takes to keep all of them on
 * the page. Pinned to the box's edge and left to grow, the toolbar under a box
 * near the right edge of a phone's page ran off the sheet; on a Hebrew screen
 * the right is where the scroll starts, so what ran past it could not be
 * scrolled to, and −, + and × were out of reach.
 *
 * Nothing is measured; the browser shares out the room. The toolbar sits in a
 * row as wide as the page, after a gap as wide as the box is from the page's
 * left edge, and only the gap may shrink. So the toolbar starts where it always
 * did while there is room for it there, and ends at the page's right edge once
 * there is not. The row is reversed on a Hebrew screen, where a row runs from
 * the right, rather than given `dir="ltr"`: the buttons would inherit that and
 * swap their order. The row lets taps through, so a tap beside the toolbar
 * still reaches the page to place a box, and only the toolbar takes them. On a
 * page narrower than the whole toolbar the buttons wrap instead of running off.
 *
 * It stays under the box at the foot of the last page too. What hangs below the
 * page can be scrolled down to, unlike what ran past its right edge.
 */
export function BoxToolbar({ anchor, children }: { anchor: SignatureAnchor; children: React.ReactNode }) {
  return (
    <div
      style={{ top: `calc(${(anchor.y + anchor.height) * 100}% + 4px)` }}
      className="absolute inset-x-0 z-10 flex rtl:flex-row-reverse pointer-events-none"
    >
      <div style={{ flex: `0 1 ${anchor.x * 100}%`, minWidth: 0 }} />
      <div
        onClick={(event) => event.stopPropagation()}
        className="pointer-events-auto flex-none max-w-full flex flex-wrap items-center gap-1 rounded-lg bg-card border border-border shadow-sm p-1"
      >
        {children}
      </div>
    </div>
  );
}

/** Where a box is drawn on its page, as percentages of the page. */
interface PageFrame {
  left: string;
  top: string;
  width: string;
  height: string;
}

/*
 * The sender's words and drawing, as part of the page. Nothing is drawn around
 * them: the stamp prints them with no frame, and Nitzan, 17.09, saw a grey
 * dotted one left around typed words on the signing page. The edge is still
 * there, unseen, so they sit exactly where the box that placed them showed
 * them, at the size worked out for its inset.
 */

/** 1.1 — the sender's words, as part of the page. */
export function PageWords({ frame, fontSize, words }: { frame: PageFrame; fontSize: number; words: string }) {
  return (
    <div
      style={{ ...frame, fontSize, color: INK }}
      className="absolute rounded border-2 border-transparent px-1 flex items-center justify-center whitespace-nowrap pointer-events-none"
    >
      {words}
    </div>
  );
}

/** 1.1, redone — the sender's drawing, as part of the page. */
export function PageDrawing({ frame, image }: { frame: PageFrame; image?: string | undefined }) {
  return (
    <div
      style={frame}
      className="absolute rounded border-2 border-transparent flex items-center justify-center pointer-events-none"
    >
      {image && <img src={image} alt="" draggable={false} className="h-full w-full object-contain" />}
    </div>
  );
}

/**
 * The number on a box in a document with more than one signer: whose it is.
 * Nothing on it to press, so a tap goes through it to what is underneath.
 */
function SignerBadge({
  owner,
  name,
  theirs,
  className,
  style,
}: {
  owner: number;
  name?: string | undefined;
  theirs: boolean;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <span
      title={name}
      style={style}
      className={cn(
        'absolute z-10 h-4 min-w-4 px-1 rounded-full text-[9px] leading-4 text-center',
        'font-semibold shadow-sm pointer-events-none',
        theirs ? 'bg-muted text-muted-foreground border border-border' : 'bg-primary text-primary-foreground',
        className,
      )}
    >
      {owner}
    </span>
  );
}

/**
 * 1.32 — what the selected box wears on its top corners: the × that removes it
 * on the end one, which is the left on a Hebrew screen, and on the start one
 * its signer's number, if it has one, which had the corner the × has now.
 *
 * Nitzan, 30.09: the × was a button in the bar under the box, a row of buttons
 * away from the place it removes. The corners are named by the writing
 * direction and not by a side, so an English screen mirrors them. The row is
 * the size of the box and lets presses through, so a press beside the × still
 * reaches the box, to be dragged.
 *
 * Only eight pixels of the × stick out past the corner, the reach the number
 * has always had: the pages scroll in a box with eight pixels of padding, and
 * a box set against the page's edge would have the rest of it cut off.
 */
export function BoxCorners({
  frame,
  owner = null,
  name,
  theirs = false,
  onRemove,
}: {
  frame: PageFrame;
  owner?: number | null;
  name?: string | undefined;
  theirs?: boolean;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div style={frame} className="absolute z-10 pointer-events-none">
      {owner !== null && (
        <SignerBadge
          owner={owner}
          name={name}
          theirs={theirs}
          className="start-0 top-0 -ms-[0.35rem] -mt-[0.45rem]"
        />
      )}
      <button
        type="button"
        aria-label={t('signing.placeRemove')}
        onClick={(event) => {
          // Not a tap on the page, which would put a second box down here.
          event.stopPropagation();
          onRemove();
        }}
        className={cn(
          'pointer-events-auto absolute end-0 top-0 -me-2 -mt-2 h-6 w-6 rounded-full',
          'flex items-center justify-center text-danger bg-card border border-border shadow-sm',
          'hover:bg-accent transition-colors',
        )}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/**
 * 1.33 — how wide the box is, between the buttons that change it: a whole
 * percent of the page's width, which is what they act on. A status, so a screen
 * reader is told the new size after a press.
 */
export function SizeReadout({ anchor }: { anchor: SignatureAnchor }) {
  const { t } = useTranslation();
  const percent = Math.round(anchor.width * 100);

  return (
    <span
      role="status"
      aria-label={t('signing.placeSizeLabel', { value: percent })}
      className="min-w-9 px-1 text-center text-[11px] font-medium tabular-nums text-muted-foreground"
    >
      {`${percent}%`}
    </span>
  );
}

/** The menus under a selected box: what signs it, or what fills it in. */
type PlaceMenu = 'signature' | 'text';

interface SignaturePlacerProps {
  /** A URL returning `application/pdf`, fetched with credentials — or the file. */
  src: string | Blob;
  anchors: SignatureAnchor[];
  /** Absent on the customer's side: there the boxes are only shown. */
  onChange?: (anchors: SignatureAnchor[]) => void;
  /**
   * How many pages the file turned out to have, once it has been read.
   *
   * The screen composing a document has no other way to know: the file has not
   * been uploaded yet, so there is no row to ask. Reported from here rather
   * than parsed a second time upstream — the bytes are already open.
   */
  onPages?: (count: number) => void;
  /**
   * What has been typed into the free-text boxes, by anchor index, and how to
   * change it. Only the signer's side passes these: on the owner's side a text
   * box is a promise about what will be asked for, and filling it in for the
   * customer is answering their document for them. A box the sender marks to
   * fill in (1.1) is not one of these — its words are on the box itself, and
   * are typed through `onChange` like the rest of the placement.
   */
  values?: Record<string, string>;
  onValue?: (index: number, text: string) => void;
  /**
   * SIG-003 — how many people are being asked to sign this document.
   *
   * More than one and every box says whose it is, because a page with four
   * signature lines on it and nothing to tell them apart is four people signing
   * in each other's places. One, and nothing is numbered at all: the document
   * nearly every business sends looks exactly as it did before.
   */
  signerCount?: number;
  /** What each of them is called, by position, for the box's tooltip. */
  signerNames?: Record<number, string>;
  /**
   * On the signer's side: which of them is holding the link. Everybody else's
   * boxes are shown — a signer can see the document is going to somebody after
   * them — but they are not theirs to sign or fill in.
   */
  mine?: number;
  /**
   * 1.50 — on the signer's side: what this signer has drawn or picked so far,
   * shown in each of their own signature boxes where the words "sign here"
   * were, so the page they are about to sign is the page they see. Absent, and
   * on every screen of the sender's, the boxes say what they always said.
   */
  mySignature?: string | null;
  /**
   * 1.31 — lets the pages reach 78% of the screen's height before they scroll
   * in their box, where they would stop at 60%.
   *
   * Asked for by the sender's screens only: marking where a signature goes is
   * done by looking at the page, and a box showing half of one at a time turns
   * that into scrolling. The customer's link does not pass it, so what they are
   * shown is the size it always was. It is also a height the box is given, not
   * one it is held to — a caller that sets its own through `className` still
   * decides.
   */
  tall?: boolean;
  className?: string;
}

export function SignaturePlacer({
  src,
  anchors,
  onChange,
  onPages,
  values,
  onValue,
  signerCount,
  signerNames,
  mine,
  mySignature,
  tall,
  className,
}: SignaturePlacerProps) {
  const { t } = useTranslation();
  const boxRef = useRef<HTMLDivElement>(null);
  /* Held in a ref so that a caller passing a fresh function each render does
     not re-fetch and re-draw the whole document. */
  const report = useRef(onPages);
  report.current = onPages;
  const [pages, setPages] = useState<RenderedPage[] | null>(null);
  const [failed, setFailed] = useState(false);
  /* 1.18 — the document once pdf.js has it, and the width its pages are fitted
     to; read once, so that a zoom paints the pages again without fetching it. */
  const [opened, setOpened] = useState<{ file: PDFDocumentProxy; boxWidth: number } | null>(null);
  /* How far in the pages are, as a multiple of the width that fits the box. */
  const [zoom, setZoom] = useState<number>(MIN_ZOOM);
  /* The column the pages are in, which a pinch scales while the fingers are down. */
  const pagesRef = useRef<HTMLDivElement>(null);
  /* Set once pages have been painted, so that a zoomed one that cannot be does
     not take the ones already on the screen away. */
  const painted = useRef(false);
  /* The place under the fingers, and where on the screen it is to be kept: from
     the moment they land until the pages have grown and it is back under them. */
  const held = useRef<{ place: Place; x: number; y: number } | null>(null);
  const inkHistory=useRef(new InkUndoHistory(anchors));
  const [canUndoInk,setCanUndoInk]=useState(false);
  const changeInk=(next:SignatureAnchor[])=>{inkHistory.current.record(anchors,next);setCanUndoInk(true);onChange?.(next);};
  const undoInk=()=>{const previous=inkHistory.current.undo(anchors);if(previous)onChange?.(previous);setCanUndoInk(previous?inkHistory.current.observe(previous):false);};
  useEffect(()=>{setCanUndoInk(inkHistory.current.observe(anchors));},[anchors]);
  const [inkTool,setInkTool] = useState<InkTool>('select');
  const [inkColor,setInkColor] = useState<string>(INK_COLORS[0]);
  const [inkWidth,setInkWidth] = useState(3);
  const [selected, setSelected] = useState<number | null>(null);
  /* The menu open under the selected box, and the buttons it hangs from. */
  const [menu, setMenu] = useState<PlaceMenu | null>(null);
  const signatureButton = useRef<HTMLButtonElement>(null);
  const textButton = useRef<HTMLButtonElement>(null);
  /* The sender's signature being asked for: for the box at `index`, or, with
     no index, drawn again over every box that has it. */
  const [signing, setSigning] = useState<{ index: number | null } | null>(null);
  /* A tap that only closes a menu. The menu closes as the finger comes down,
     so by the time the tap reaches the page there is nothing to say it was not
     a tap to put a box there. */
  const dismissing = useRef(false);
  /* The box just made one the sender types into, for the words to go straight in. */
  const typeInto = useRef<number | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  const readOnly = !onChange;
  /* Numbered only when there is somebody to tell apart from. */
  const numbered = (signerCount ?? 1) > 1;

  /*
   * The drag in progress, if there is one. A ref and not state: it changes on
   * every pointer event and none of it is drawn — what is drawn is the anchor,
   * which the parent already owns. `moved` is what tells the click that follows
   * a drag from the click that follows a tap.
   */
  const dragging = useRef<{
    index: number;
    pointer: number;
    /* What holds the pointer, to let it go of it if a second finger lands. */
    target: HTMLElement;
    startX: number;
    startY: number;
    origin: SignatureAnchor;
    moved: boolean;
  } | null>(null);

  /**
   * 1.1 — lets go of the selected box: its bar goes, and a box the sender has
   * filled in loses its frame with it. The caret goes too. A focused box draws
   * a ring of its own, and not every browser takes focus away for a tap on
   * something that cannot hold it, which would leave the ring around words
   * that are meant to look printed.
   */
  const letGo = useCallback(() => {
    setSelected(null);
    const focused = window.document.activeElement;
    if (focused instanceof HTMLElement && boxRef.current?.contains(focused)) focused.blur();
  }, []);

  /*
   * 1.1 — a tap anywhere else on the screen lets go of the selected box too:
   * the sender taps away from the words once they are typed, wherever the thumb
   * happens to be, not only on the page. The placer's own taps are marked on
   * their way down and left to it: the menu and the signature dialog are drawn
   * elsewhere in the document but are still the placer's, and React passes
   * their clicks through it. Clicks rather than presses, so a finger that
   * scrolls the screen lets nothing go.
   */
  const ownClick = useRef(false);
  useEffect(() => {
    if (readOnly) return;
    const unmark = () => {
      ownClick.current = false;
    };
    const away = () => {
      if (!ownClick.current) letGo();
    };
    // Captured on the document, so every click starts unmarked before React sees it.
    window.document.addEventListener('click', unmark, true);
    window.document.addEventListener('click', away);
    return () => {
      window.document.removeEventListener('click', unmark, true);
      window.document.removeEventListener('click', away);
    };
  }, [readOnly, letGo]);

  useEffect(() => {
    let alive = true;
    setOpened(null);
    setPages(null);
    painted.current = false;
    setFailed(false);

    const open = async () => {
      const [{ getDocument, GlobalWorkerOptions }, { default: PdfWorker }] = await Promise.all([
        import('pdfjs-dist/legacy/build/pdf.mjs'),
        import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker'),
      ]);
      GlobalWorkerOptions.workerPort = new PdfWorker();

      const bytes = new Uint8Array(await bytesOf(src));
      const file = await getDocument({ data: bytes, standardFontDataUrl }).promise;
      if (alive) report.current?.(file.numPages);

      const box = boxRef.current;
      const padding = box
        ? parseFloat(getComputedStyle(box).paddingInlineStart) + parseFloat(getComputedStyle(box).paddingInlineEnd)
        : 0;
      const boxWidth = Math.max((box?.clientWidth ?? 320) - padding, 160);
      if (alive) setOpened({ file, boxWidth });
    };

    open().catch((error: unknown) => {
      console.error('Could not render the signing document', error);
      if (alive) setFailed(true);
    });

    return () => {
      alive = false;
    };
  }, [src]);

  /*
   * 1.18 — the pages, painted at the zoom they are at: once at 1× when the
   * document is open, and again each time a pinch lets go. The pages already on
   * the screen stay there until the new ones are ready, stretched to the new
   * size by the browser in the meantime, so nothing blanks while it is drawn.
   */
  useEffect(() => {
    if (!opened) return;
    let alive = true;

    const draw = async () => {
      const { file, boxWidth } = opened;
      const density = Math.min(window.devicePixelRatio || 1, 2);

      /*
       * The placement limit counts the pages of the file that was uploaded. A
       * signed copy is that file plus the certificate page, and it is this same
       * component that shows it — so capping at the placement limit exactly
       * would drop the certificate off a document that used every page it was
       * allowed, hiding the one page that proves the signature.
       *
       * Placing never reaches the extra page: a draft is only ever shown its own
       * source, and import refuses a source above the limit. The server checks
       * every anchor against the document's real page count regardless.
       */
      const maxPages = LIMITS.maxSignaturePages + 1;

      const sheets: { number: number; page: PDFPageProxy; scale: number; width: number; height: number }[] = [];
      for (let number = 1; number <= Math.min(file.numPages, maxPages); number += 1) {
        if (!alive) return;
        const page = await file.getPage(number);
        // `getViewport` already applies the page's own /Rotate, so what is
        // measured here is the page as a reader sees it — which is the frame
        // the anchor is a fraction of.
        const unscaled = page.getViewport({ scale: 1 });
        const scale = boxWidth / unscaled.width;
        // Each page at 1× first, for how sharp they can all be painted at once.
        const fitted = page.getViewport({ scale: scale * density });
        sheets.push({
          number,
          page,
          scale,
          width: Math.floor(fitted.width / density),
          height: Math.floor(fitted.height / density),
        });
      }

      const sharpness = canvasDensity(sheets, zoom, density);

      const drawn: RenderedPage[] = [];
      for (const { number, page, scale, width, height } of sheets) {
        if (!alive) return;
        const viewport = page.getViewport({ scale: scale * sharpness });

        const canvas = window.document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.className = 'absolute inset-0 w-full h-full rounded';

        const context = canvas.getContext('2d');
        if (!context) throw new Error('no 2d context');
        await page.render({ canvas, canvasContext: context, viewport }).promise;

        drawn.push({ number, canvas, width, height });
      }

      if (alive) {
        painted.current = true;
        setPages(drawn);
      }
    };

    draw().catch((error: unknown) => {
      console.error('Could not render the signing document', error);
      // Pages that cannot be painted larger leave the ones already there: they
      // are only less sharp, and they still show where the boxes go.
      if (alive && !painted.current) setFailed(true);
    });

    return () => {
      alive = false;
    };
  }, [opened, zoom]);

  /* The canvas is painted by pdf.js rather than rendered, so it is put into the
     page wrapper by hand and the boxes above it stay ordinary React children.
     A page painted again (1.18) brings a new canvas, which takes the old one's place. */
  const mount = useCallback((element: HTMLDivElement | null, page: RenderedPage) => {
    if (!element || element.firstChild === page.canvas) return;
    element.querySelector(':scope > canvas')?.remove();
    element.prepend(page.canvas);
  }, []);

  /**
   * 1.1, a third time — a tap on the page puts the recipient's signature there
   * at once, selected, with its bar under it. Nitzan, 17.09, on v19: a dialog
   * opening before the box is on the page is strange; the box has to be there
   * first, to see where it lands. What goes in it is changed from the bar.
   */
  const placeAt = (page: RenderedPage, event: React.MouseEvent<HTMLDivElement>) => {
    if (dismissing.current) {
      dismissing.current = false;
      return;
    }
    if (!onChange) return;
    // 1.1 — the tap that takes the frame off the sender's finished words or
    // drawing only lets go of them, and puts nothing down where it landed.
    if (pageTap(anchors, selected) === 'let-go') {
      letGo();
      return;
    }
    if (anchors.length >= LIMITS.maxSignatureAnchors) return;
    const rect = event.currentTarget.getBoundingClientRect();
    // Centred on the tap, then pulled back inside the page: a box placed near
    // the edge would otherwise hang off it, and the server refuses those.
    const x = Math.min(Math.max((event.clientX - rect.left) / rect.width - DEFAULT_WIDTH / 2, 0), 1 - DEFAULT_WIDTH);
    const y = Math.min(Math.max((event.clientY - rect.top) / rect.height - DEFAULT_HEIGHT / 2, 0), 1 - DEFAULT_HEIGHT);

    const frame: BoxFrame = { page: page.number, x, y, width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
    onChange(addBox(anchors, frame, 'signer-signature'));
    // The new box is last in the list, and selected so its bar is at hand.
    setSelected(anchors.length);
  };

  /**
   * An option picked from a menu under the box at `index`. The sender's
   * signature is drawn once for the whole document, so the dialog is opened
   * only while there is no drawing to sign the box with, and closing it leaves
   * the box as it was.
   */
  const choose = (index: number, role: AnchorRole) => {
    setMenu(null);
    if (!onChange) return;
    if (role === 'sender-signature' && !senderSignatureOf(anchors)) {
      setSigning({ index });
      return;
    }
    const next = withRole(anchors, index, role);
    // Picking what a box already is changes nothing, so there is nothing to save.
    if (next === anchors) return;
    onChange(next);
    if (role === 'sender-text') typeInto.current = index;
  };

  /**
   * A signature saved in the dialog: the box it was asked for becomes the
   * sender's signature, and the signature goes on every box that is, since the
   * document has the one.
   */
  const saveSignature = (index: number | null, image: string) => {
    setSigning(null);
    if (!onChange) return;
    onChange(signAsSender(index === null ? anchors : withRole(anchors, index, 'sender-signature'), image));
  };

  /**
   * Resizing keeps the box's centre where it was put.
   *
   * Growing from the corner walks the signature away from the line it was
   * placed on, one press at a time, and the placer has to drag it back — with
   * no drag. Growing from the middle leaves it where they aimed.
   */
  const resize = (index: number, factor: number) => {
    if (!onChange) return;
    const anchor = anchors[index];
    if (!anchor) return;

    const width = Math.min(Math.max(anchor.width * factor, MIN_WIDTH), MAX_WIDTH);
    const height = (anchor.height / anchor.width) * width;
    const x = Math.min(Math.max(anchor.x + (anchor.width - width) / 2, 0), 1 - width);
    const y = Math.min(Math.max(anchor.y + (anchor.height - height) / 2, 0), Math.max(1 - height, 0));

    onChange(anchors.map((current, at) => (at === index ? { ...current, x, y, width, height } : current)));
  };

  /**
   * Dragging a box that is already selected.
   *
   * The gesture is measured against the page it is on, so the box follows the
   * finger at whatever scale the preview happens to be. The pointer is captured
   * on the box, which keeps the move events coming when the finger outruns the
   * box — and lets the box be dragged right up against the edge of the page
   * instead of being dropped the moment the pointer leaves it.
   */
  const startDrag = (index: number, event: React.PointerEvent<HTMLElement>) => {
    const origin = anchors[index];
    if (!onChange || selected !== index || !origin) return;
    dragging.current = {
      index,
      pointer: event.pointerId,
      target: event.currentTarget,
      startX: event.clientX,
      startY: event.clientY,
      origin,
      moved: false,
    };
    /* Capture is an improvement, not the mechanism: a browser that refuses this
       pointer still drags, it just stops when the finger outruns the box. */
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* no capture, still draggable */
    }
  };

  const onDrag = (page: RenderedPage, event: React.PointerEvent<HTMLElement>) => {
    const drag = dragging.current;
    if (!drag || drag.pointer !== event.pointerId || !onChange) return;

    const travelled = Math.abs(event.clientX - drag.startX) + Math.abs(event.clientY - drag.startY);
    if (!drag.moved && travelled < DRAG_SLOP) return;
    drag.moved = true;

    const dx = (event.clientX - drag.startX) / page.width;
    const dy = (event.clientY - drag.startY) / page.height;
    onChange(dragAnchor(anchors, drag.index, drag.origin, dx, dy));
  };

  const endDrag = (event: React.PointerEvent<HTMLElement>) => {
    const drag = dragging.current;
    if (!drag || drag.pointer !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    // Kept until the click has been and gone: the click fires after the pointer
    // is up, and a drag must not also toggle the selection off.
    if (!drag.moved) dragging.current = null;
  };

  const removeAt = (index: number) => {
    if (!onChange) return;
    onChange(anchors.filter((_, at) => at !== index));
    setSelected(null);
  };

  /**
   * 1.18 — a second finger on the pages is a pinch, and a pinch is not a drag.
   * The box goes back to where the first finger found it and the pointer is let
   * go of, so the moves that follow are nobody's, and the lift that ends them is
   * not a tap on the box.
   */
  const cancelDrag = () => {
    const drag = dragging.current;
    if (!drag) return;
    dragging.current = null;
    const back = undoDrag(anchors, drag);
    if (back && onChange) onChange(back);
    if (drag.target.hasPointerCapture(drag.pointer)) drag.target.releasePointerCapture(drag.pointer);
  };

  /*
   * 1.18 — pinching the pages. When the fingers land, the place between them is
   * noted as a fraction of its page, and the column of pages is scaled from
   * there while they move, so what is under the fingers stays under them. When
   * one lifts, the zoom is settled and the pages are laid out at it; the place is
   * measured as the fingers left it, for the layout effect below to put it back.
   */
  const pinch = usePinchZoom({
    zoom,
    min: MIN_ZOOM,
    max: MAX_ZOOM,
    target: pagesRef,
    onStart: (centre) => {
      cancelDrag();
      const box = boxRef.current;
      const column = pagesRef.current;
      if (!box || !column) return;
      const at = column.getBoundingClientRect();
      column.style.transformOrigin = `${centre.x - at.left}px ${centre.y - at.top}px`;
      const place = placeUnder(pageRects(box), centre.x, centre.y);
      held.current = place && { place, ...centre };
    },
    onEnd: (next) => {
      const place = held.current?.place;
      const page = place && boxRef.current?.querySelector<HTMLElement>(`[data-page="${place.page}"]`);
      held.current = next !== zoom && place && page ? { place, ...pointOn(place, page.getBoundingClientRect()) } : null;
      if (next !== zoom) setZoom(next);
    },
  });

  /* The pages have their new size by now and the scroll has not moved, so what
     was under the fingers is somewhere else. It is brought back before anything
     is drawn, so nobody sees it go. */
  useLayoutEffect(() => {
    const keep = held.current;
    held.current = null;
    const box = boxRef.current;
    const page = keep && box?.querySelector<HTMLElement>(`[data-page="${keep.place.page}"]`);
    if (!keep || !box || !page) return;
    const now = pointOn(keep.place, page.getBoundingClientRect());
    box.scrollLeft += now.x - keep.x;
    box.scrollTop += now.y - keep.y;
  }, [zoom]);

  if (failed) {
    return (
      <div
        className={cn(
          'w-full h-[40vh] min-h-[14rem] rounded-xl border border-border bg-muted/60',
          'flex flex-col items-center justify-center gap-2 text-center px-4',
          className,
        )}
      >
        <FileText className="h-6 w-6 text-muted-foreground/60" />
        <p className="text-sm text-muted-foreground">{t('common.documentFailed')}</p>
      </div>
    );
  }

  /* 1.18 — the pages at the size they are on the screen. A box is a fraction of
     its page, so it grows with the page; and a drag, and a line typed into a
     box, are measured against this and not the size at 1×. */
  const shown = pages?.map((page) => ({ ...page, width: page.width * zoom, height: page.height * zoom }));

  return (
    <div
      ref={boxRef}
      /*
       * `overscroll-auto`, deliberately, and not `contain`.
       *
       * Containment is for a scroller layered over a page that must not move
       * underneath it — a modal, a drawer. This box is inline content in the
       * middle of a form, and containing it means the scroll dies at the last
       * page: on the compose screen the reader hits the bottom of the document
       * and the screen stops, with the channels and the send button below a fold
       * they cannot reach without lifting the finger off the document first.
       * Chaining hands the rest of the gesture to the page, which is what every
       * other block of content on it does.
       */
      className={cn(
        'w-full overflow-auto overscroll-auto [scrollbar-gutter:stable]',
        // 1.18 — one finger still scrolls, and two are the pinch's, which the
        // screen would otherwise take to zoom itself, buttons and all.
        '[touch-action:pan-x_pan-y]',
        tall ? 'max-h-[78vh]' : 'max-h-[60vh]',
        'rounded-xl border border-border bg-muted/60 p-2 flex flex-col',
        className,
      )}
      {...pinch}
      /* 1.1 — a click that starts in here, or in a menu or dialog opened from
         here, is the placer's own, and the document does not let go for it. */
      onClickCapture={() => {
        ownClick.current = true;
      }}
      /* The grey around and between the pages is not the page: a tap there
         lets go of the box and puts nothing down. */
      onClick={(event) => {
        if (!readOnly && (event.target === event.currentTarget || event.target === pagesRef.current)) letGo();
      }}
    >
      {onChange && <InkToolbar tool={inkTool} onTool={setInkTool} color={inkColor} onColor={setInkColor} width={inkWidth} onWidth={setInkWidth} onUndo={undoInk} canUndo={canUndoInk} onZoomIn={()=>setZoom(value=>Math.min(MAX_ZOOM,value+.25))} onZoomOut={()=>setZoom(value=>Math.max(MIN_ZOOM,value-.25))}/>}
      {!pages && <p className="m-auto text-sm text-muted-foreground">{t('common.loading')}</p>}

      {/* 1.18 — the pages in a column of their own, for the pinch to scale.
          The pages centre themselves with auto margins and not the column's
          `items-center`: a page wider than the box has no margin to give, so it
          starts at the edge and scrolls, where a centred one overflows both
          ways and its start could not be scrolled to. */}
      <div ref={pagesRef} className="flex flex-col space-y-3 shrink-0">
      {shown?.map((page) => (
        <div
          key={page.number}
          data-page={page.number}
          ref={(element) => mount(element, page)}
          // Every press on the page notes whether a menu was open as it came
          // down: a tap that closes a menu does not also put a box down.
          onPointerDown={() => {
            dismissing.current = menu !== null;
          }}
          onClick={(event) => placeAt(page, event)}
          style={{ width: page.width, height: page.height }}
          className={cn('relative mx-auto shrink-0 shadow-sm rounded', !readOnly && 'cursor-crosshair')}
        >
          {onChange && inkTool !== 'select' && <InkSurface page={page.number} width={page.width} height={page.height} tool={inkTool} color={inkColor} strokeWidth={inkWidth} anchors={anchors} onChange={changeInk}/>}
          {anchors.map((anchor, index) => {
            if (anchor.page !== page.number) return null;
            const view = boxView(anchor, { placing: !readOnly, answering: Boolean(onValue) });
            const frame = {
              // Physical left/top: the page's own origin is its left edge in
              // both languages, and logical properties would mirror the box
              // onto the wrong side of an RTL screen.
              left: `${anchor.x * 100}%`,
              top: `${anchor.y * 100}%`,
              width: `${anchor.width * 100}%`,
              height: `${anchor.height * 100}%`,
            };
            /* The box in pixels on this page, for a line typed into it to be
               fitted to. */
            const room = { width: anchor.width * page.width, height: anchor.height * page.height };
            /* 1.32 — the selected box wears the button that removes it, on the
               screen that places boxes. Nobody is selected on the signing page. */
            const removable = !readOnly && selected === index;

            /* 1.1 — the sender's own words, typed into the box on the screen
               that places it, at the size the box sets them in on the page. It
               is still a box being placed: a tap selects it, and once selected
               it drags like any other. */
            if (view === 'sender-input') {
              const placeholder = t('signing.placeSenderText');
              return (
                <Fragment key={index}>
                <input
                  ref={(element) => {
                    // Just picked to fill in yourself: the words go straight in.
                    if (element && typeInto.current === index) {
                      typeInto.current = null;
                      element.focus();
                    }
                  }}
                  type="text"
                  maxLength={200}
                  value={anchor.text ?? ''}
                  placeholder={placeholder}
                  aria-label={placeholder}
                  onChange={(event) =>
                    onChange?.(
                      anchors.map((current, at) => (at === index ? { ...current, text: event.target.value } : current)),
                    )
                  }
                  onFocus={() => setSelected(index)}
                  onClick={(event) => {
                    event.stopPropagation();
                    // A second tap puts the caret between the words, so unlike
                    // the other boxes it does not let go of the selection.
                    if (dragging.current?.moved) dragging.current = null;
                    setSelected(index);
                  }}
                  onPointerDown={(event) => startDrag(index, event)}
                  onPointerMove={(event) => onDrag(page, event)}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                  style={{
                    ...frame,
                    touchAction: selected === index ? 'none' : undefined,
                    fontSize: typedFontSize(room, widthAtOnePixel(anchor.text || placeholder)),
                    color: INK,
                  }}
                  className={cn(
                    'absolute rounded border-2 px-1 text-center placeholder:text-primary/60',
                    'focus:outline-none focus:ring-2 focus:ring-primary',
                    // 1.1 — once words are in and the box is let go of, only
                    // the words are left; a tap on them brings the frame back.
                    INPUT_OUTLINE[boxOutline(anchor, selected === index)],
                  )}
                />
                {removable && <BoxCorners frame={frame} onRemove={() => removeAt(index)} />}
                </Fragment>
              );
            }

            /* 1.1 — the same words everywhere else, as part of the page: on the
               signing page, which does not ask for them, and on the sender's
               screen once the document has gone out. Nothing on it to press, so
               a tap lands on the page as it would on printed text. Not clipped
               either: a line measured a pixel short spills past its frame rather
               than losing its last letter. */
            if (view === 'sender-text') {
              const words = anchor.text?.trim() ?? '';
              return (
                <PageWords
                  key={index}
                  frame={frame}
                  fontSize={typedFontSize(room, widthAtOnePixel(words))}
                  words={words}
                />
              );
            }

            /* 1.1, redone — the sender's drawing everywhere but the screen that
               places it: on the signing page, and on the sender's screen once
               the document has gone out. Part of the page, as their words are,
               and fitted into the box as the stamp fits it. */
            if (view === 'sender-signature') {
              return <PageDrawing key={index} frame={frame} image={anchor.image} />;
            }

            /* Whose box this is. The sender's own signature, the one box of the
               sender's that gets this far, is nobody's (1.1). A box with no
               signer is the first signer's: every box placed before there could
               be a second one is theirs. */
            const owner = anchorSigner(anchor);
            const theirs = mine !== undefined && owner !== null && owner !== mine;
            const name = owner === null ? undefined : signerNames?.[owner];
            /* Pinned to the box's own top-left corner, in the page's
               coordinates rather than the box's: neither an input nor a button
               can carry a child, so the number is a sibling that happens to sit
               on the corner. Not on the selected box (1.32): the × is on that
               corner, and the number is carried over to the other one. */
            const badge = numbered && owner !== null && !removable ? (
              <SignerBadge
                key={`signer-${index}`}
                owner={owner}
                name={name}
                theirs={theirs}
                style={{ left: `calc(${anchor.x * 100}% - 0.35rem)`, top: `calc(${anchor.y * 100}% - 0.45rem)` }}
              />
            ) : null;

            /* A box asking to be typed in is an input on the signer's side, at
               the size it will be printed: the answer is written on the
               document, in the space the sender set aside for it, rather than
               in a list underneath that has to be matched back to the page. */
            if (view === 'answer' && onValue) {
              const typed = values?.[String(index)] ?? '';
              const placeholder = theirs ? (name ?? '') : t('publicSign.typeHere');
              return (
                <Fragment key={index}>
                  {badge}
                  <input
                    type="text"
                    maxLength={200}
                    /* Somebody else's blank is shown as it will be printed and
                       cannot be answered here: filling it in would be putting
                       words in the next signer's mouth. */
                    readOnly={theirs}
                    value={typed}
                    onChange={(event) => onValue(index, event.target.value)}
                    onClick={(event) => event.stopPropagation()}
                    placeholder={placeholder}
                    /* 1.1 — sized to the line typed, which is what kept the rest
                       of a long answer out of sight when only the box set it. */
                    style={{
                      ...frame,
                      fontSize: typedFontSize(room, widthAtOnePixel(typed || placeholder)),
                      ...(theirs ? {} : { color: INK }),
                    }}
                    className={cn(
                      'absolute rounded border-2 px-1 text-center',
                      'focus:outline-none focus:ring-2 focus:ring-primary',
                      theirs
                        ? 'border-dotted border-border bg-muted/50 text-muted-foreground placeholder:text-muted-foreground'
                        : 'border-primary bg-primary/10 placeholder:text-primary/60',
                    )}
                  />
                </Fragment>
              );
            }

            /* What the box says. On the screen that places it, which of the
               four options it is (1.1, redone), and for the sender's own
               signature the drawing itself once there is one — a picture a
               press goes straight through, so a drag on it moves the box and
               not a copy of the picture. Everywhere else, what a box has always
               said. */
            let content: React.ReactNode;
            if (view === 'sender-sign') {
              content = anchor.image ? (
                <img
                  src={anchor.image}
                  alt=""
                  draggable={false}
                  className="h-full w-full object-contain pointer-events-none"
                />
              ) : (
                t('signing.placeRoleSenderSignature')
              );
            } else if (theirs) {
              content = name ?? t('publicSign.signHere');
            } else if (!readOnly) {
              content = t(anchor.kind === 'text' ? 'signing.placeRoleSignerText' : 'signing.placeRoleSignerSignature');
            } else if (anchor.kind === 'text') {
              content = values?.[String(index)] ?? t('signing.placeText');
            } else if (mySignature) {
              /* 1.50 — what the signer has signed with so far, where the words
                 were: a picture a press goes through, fitted into the box. */
              content = (
                <img
                  src={mySignature}
                  alt={t('publicSign.signHere')}
                  draggable={false}
                  className="h-full w-full object-contain pointer-events-none"
                />
              );
            } else {
              content = t('publicSign.signHere');
            }

            return (
              <Fragment key={index}>
                {badge}
              <button
                type="button"
                onClick={(event) => {
                  // A tap on an existing box selects it. Without this the tap
                  // reaches the page underneath, which puts a second box down
                  // on top of the first.
                  event.stopPropagation();
                  // The click that ends a drag is not a tap: it would otherwise
                  // deselect the very box that was just moved, and take the
                  // buttons away from someone about to move it again.
                  if (dragging.current?.moved) {
                    dragging.current = null;
                    return;
                  }
                  setSelected(selected === index ? null : index);
                }}
                onPointerDown={(event) => startDrag(index, event)}
                onPointerMove={(event) => onDrag(page, event)}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                disabled={readOnly || theirs}
                style={{
                  // Only the selected box swallows the gesture. An unselected
                  // one leaves it to the page, so a finger landing anywhere on
                  // the document still scrolls it.
                  touchAction: selected === index ? 'none' : undefined,
                  ...frame,
                }}
                className={cn(
                  'absolute rounded border-2 flex items-center justify-center',
                  'text-[10px] font-medium transition-colors',
                  // 1.1 — a signature the sender put down, once let go of, is
                  // only the drawing; every other box keeps its frame.
                  theirs
                    ? 'border-dotted border-border bg-muted/50 text-muted-foreground'
                    : BOX_OUTLINE[boxOutline(anchor, selected === index)],
                  readOnly && 'cursor-default',
                )}
                /* A drawing says nothing to a screen reader, so the box says
                   what it is instead. */
                aria-label={view === 'sender-sign' ? t('signing.placeRoleSenderSignature') : undefined}
                title={
                  theirs
                    ? name
                    : !readOnly && selected === index
                      ? t('signing.placeDragHint')
                      : undefined
                }
              >
                {content}
              </button>
                {removable && (
                  <BoxCorners
                    frame={frame}
                    owner={numbered ? owner : null}
                    name={name}
                    theirs={theirs}
                    onRemove={() => removeAt(index)}
                  />
                )}
              </Fragment>
            );
          })}

          {/* The controls for the selected box sit under it rather than on it:
              a signature box is small, and buttons inside one cover the very
              thing being positioned. The one on its frame is the ×, at the
              corner (1.32). */}
          {!readOnly &&
            selected !== null &&
            anchors[selected]?.page === page.number &&
            (() => {
              const anchor = anchors[selected]!;
              const copied = copyForNextSigner(anchors, selected);
              const isText = anchor.kind === 'text';
              return (
                <BoxToolbar anchor={anchor}>
                  {/* 1.1, a third time — what goes in this place: a signature
                      button and a text button, each with a menu of yourself or
                      the recipient, and the one the box is lit. Nitzan, 17.09.
                      A signer given a pen where the form wanted a licence
                      number draws their name over the blank, and the number is
                      nowhere, so which of the two it is matters as much as who.

                      1.33 — they open the bar, each with a small arrow down to
                      say that it opens a menu. Then copy, bigger, the size and
                      smaller, and the buttons only some boxes have. */}
                  <button
                    ref={signatureButton}
                    type="button"
                    aria-label={t('signing.placeSignatureMenu')}
                    aria-haspopup="menu"
                    aria-expanded={menu === 'signature'}
                    onClick={() => setMenu((open) => (open === 'signature' ? null : 'signature'))}
                    className={cn(
                      'h-7 gap-0.5 px-1.5 rounded flex items-center justify-center hover:bg-accent transition-colors',
                      !isText && 'bg-primary/10 text-primary',
                    )}
                  >
                    <Signature className="h-3.5 w-3.5" />
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  <button
                    ref={textButton}
                    type="button"
                    aria-label={t('signing.placeTextMenu')}
                    aria-haspopup="menu"
                    aria-expanded={menu === 'text'}
                    onClick={() => setMenu((open) => (open === 'text' ? null : 'text'))}
                    className={cn(
                      'h-7 gap-0.5 px-1.5 rounded flex items-center justify-center hover:bg-accent transition-colors',
                      isText && 'bg-primary/10 text-primary',
                    )}
                  >
                    <Type className="h-3.5 w-3.5" />
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  {/* Drawn over the page rather than inside the bar, and still
                      rendered in it: a click in the menu goes up through the
                      bar, which keeps it from reaching the page. */}
                  <AnchoredMenu
                    open={menu !== null}
                    onClose={closeMenu}
                    anchorRef={menu === 'text' ? textButton : signatureButton}
                    width={216}
                  >
                    {menu && (
                      <PlaceRoleOptions menu={menu} current={anchorRole(anchor)} onChoose={(role) => choose(selected, role)} />
                    )}
                  </AnchoredMenu>
                  {/* SIG-003 — copy makes the *next* signer's box, not another
                      of the same one's. A second box for the same person is a
                      second place for one signature; what a form with two lines
                      on it is actually asking for is two people. Correcting
                      that — a set of initials the same person signs twice — is
                      what the numbered button in this bar is for.

                      Next after the last signer on the document, whichever box
                      is copied; disabled once every signer allowed has a box or
                      the document has as many boxes as it may — both decided
                      in copyForNextSigner. */}
                  <button type="button"
                    disabled={!copied}
                    aria-label={t('signing.placeForNextSigner')}
                    onClick={() => {
                      if (!copied) return;
                      onChange?.(copied);
                      // The copy is last in the list, so its index is the length before it.
                      setSelected(anchors.length);
                    }}
                    className="h-7 w-7 rounded flex items-center justify-center hover:bg-accent disabled:opacity-40">
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label={t('signing.placeBigger')}
                    onClick={() => resize(selected, STEP)}
                    className="h-7 w-7 rounded flex items-center justify-center hover:bg-accent transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                  <SizeReadout anchor={anchor} />
                  <button
                    type="button"
                    aria-label={t('signing.placeSmaller')}
                    onClick={() => resize(selected, 1 / STEP)}
                    className="h-7 w-7 rounded flex items-center justify-center hover:bg-accent transition-colors"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  {/* Whose box this is, cycled. Only once there is somebody to
                      cycle through: on a document with one signer the number
                      would be a control that does nothing. Never on a box of
                      the sender's, words or signature, which is none of theirs
                      (1.1). */}
                  {numbered && !isSenderBox(anchor) && (
                    <button
                      type="button"
                      aria-label={t('signing.placeSigner', { number: anchor.signer ?? 1 })}
                      onClick={() =>
                        onChange?.(
                          anchors.map((current, at) =>
                            at === selected
                              ? { ...current, signer: ((current.signer ?? 1) % (signerCount ?? 1)) + 1 }
                              : current,
                          ),
                        )
                      }
                      className="h-7 w-7 rounded flex items-center justify-center hover:bg-accent transition-colors text-[11px] font-semibold text-primary"
                    >
                      {anchor.signer ?? 1}
                    </button>
                  )}
                  {/* The sender's signature, drawn again. The document has one
                      drawing, so the new one replaces it on every box marked
                      for it. */}
                  {isSenderSignature(anchor) && (
                    <button
                      type="button"
                      aria-label={t('signing.senderSignatureRedo')}
                      onClick={() => setSigning({ index: null })}
                      className="h-7 w-7 rounded flex items-center justify-center hover:bg-accent transition-colors"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                    </button>
                  )}
                </BoxToolbar>
              );
            })()}
        </div>
      ))}
      </div>

      {/* 1.1, redone — the signature dialog, beside the pages and not in one.
          React hands a click inside a dialog up through the components it was
          rendered in, wherever on the screen the dialog is drawn, and a click
          that reached a page would put a box down under the dialog. It is
          mounted only while it asks, so the pad is laid out in an open dialog
          before it measures itself. */}
      {signing && (
        <SenderSignatureDialog
          onSave={(image) => saveSignature(signing.index, image)}
          onClose={() => setSigning(null)}
        />
      )}
    </div>
  );
}
